import { useState, useEffect, createContext, useContext } from "react";
import { Redirect, useHistory } from "react-router-dom";

import {
  Container,
  Box,
  Grid,
  Typography,
  Button,
  useTheme
} from "@mui/material";

import { AnimatedLogo } from "@aclymatepackages/atoms";
import { CompanyDataConfirmation } from "@aclymatepackages/modules";
import { personalEmailDomains } from "@aclymatepackages/lists";

import SignInForm from "./SignInForm";
import ResendVerificationEmailButton from "./ResendVerificationEmailButton";

import OnboardingImageLayoutBackdrop from "../layouts/OnboardingImageLayoutBackdrop";
import CompanyDescriptionNameSearch from "../modules/CompanyDescriptionNameSearch";

import { fetchOurApi, fetchNaicsCodes } from "../../helpers/utils/apiCalls";
import { useAuth } from "../../helpers/firebase";
import { sendExistingCompanyAdminEmail } from "../../helpers/components/accountAccess";
import { analyticsTrack, triggerGoogleEvent } from "../../helpers/analytics";
import { findGoogleAddressStringDetails } from "../../helpers/utils/geography";
import { getUrlSearchParams } from "../../helpers/otherHelpers";

const OnboardingContext = createContext();

const personalEmailDomainSet = new Set(personalEmailDomains);

const isPersonalEmailWebsiteUrl = (websiteUrl) =>
  !!websiteUrl && personalEmailDomainSet.has(websiteUrl.toLowerCase());

const ExistingCompanyWithUserError = ({ existingCompanyWithUserError }) => {
  const { palette } = useTheme();

  return (
    <>
      {existingCompanyWithUserError && (
        <Grid item>
          <Typography variant="h6" color={palette.error.main} align="center">
            An account already exists for this company. We&apos;ve let this account&apos;s
            administrator know that you&apos;ve requested access to their account.
            You&apos;ll be informed by email once they&apos;ve approved you
          </Typography>
        </Grid>
      )}
    </>
  );
};

const PageLoadingAnimation = ({ caption }) => (
  <Box display="flex" justifyContent="center">
    <Grid container spacing={2} direction="column" alignItems="center">
      <Grid item>
        <AnimatedLogo />
      </Grid>
      {caption && (
        <Grid item>
          <Typography variant="h6" align="center">
            {caption}
          </Typography>
        </Grid>
      )}
    </Grid>
  </Box>
);

const CompanyDataConfirmationStep = ({ companyData }) => {
  const history = useHistory();
  const [user] = useAuth();

  const { setVerifyAccountLoading } = useContext(OnboardingContext);

  const onConfirmClick = async (companyData = {}) => {
    const platformRedirectCallback = ({ companyId }) => {
      if (!companyId) {
        return setVerifyAccountLoading(false);
      }
      window.sessionStorage.setItem("accountId", companyId);
      triggerGoogleEvent("onboarding_finished");
      analyticsTrack("finished onboarding");
      history.push("/platform/company/onboarding");
      return window.location.reload();
    };

    const { address } = companyData;
    const { place_id, description } = address || {};
    const googleAddressStringDetails =
      await findGoogleAddressStringDetails(place_id);

    setVerifyAccountLoading(true);
    return await fetchOurApi({
      path: `/verification/verify-company-data`,
      method: "POST",
      data: {
        user,
        ...companyData,
        address: { place_id, description, ...googleAddressStringDetails }
      },
      callback: platformRedirectCallback
    });
  };

  return (
    <CompanyDataConfirmation
      companyData={companyData}
      onConfirmClick={onConfirmClick}
      fetchNaicsCodes={fetchNaicsCodes}
    />
  );
};

const OnboardingSiginIn = () => {
  const [userEmail, setUserEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountAccessError, setAccountAccessError] = useState("");

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Typography variant="h4" color="secondary">
          Welcome back
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Please log into your account to continue onboarding
        </Typography>
      </Grid>
      <SignInForm
        userEmail={userEmail}
        setUserEmail={setUserEmail}
        password={password}
        setPassword={setPassword}
        setAccountAccessError={setAccountAccessError}
      />
      <Grid item>
        <Typography align="center" color="error">
          {accountAccessError}
        </Typography>
      </Grid>
    </Grid>
  );
};

const ErrorMessage = ({ status = "error", email }) => {
  const statusMessages = {
    error:
      "We couldn't find a company to verify. Please make sure that you have the right URL from the email we sent you",
    expired:
      "Your verification token is expired. Click the button below to generate a new verification token"
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Typography variant="h6" align="center">
          {statusMessages[status]}
        </Typography>
      </Grid>
      <Grid item container justifyContent="center">
        <ResendVerificationEmailButton email={email} />
      </Grid>
    </Grid>
  );
};

const CompanyMatchConfirmation = ({
  matchedCompanyName,
  onDecision,
  decisionLoading
}) => (
  <Grid container spacing={2} direction="column" alignItems="center">
    <Grid item>
      <Typography variant="h6" align="center">
        We found an existing company that looks like a match: &quot;
        {matchedCompanyName}&quot;
      </Typography>
      <Typography variant="body1" align="center" color="textSecondary">
        Is that your company?
      </Typography>
    </Grid>
    <Grid item container spacing={2} justifyContent="center">
      <Grid item>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => onDecision("merge")}
          disabled={decisionLoading}
        >
          Yes, that&apos;s my company
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant="outlined"
          onClick={() => onDecision("new")}
          disabled={decisionLoading}
        >
          No, create a new company
        </Button>
      </Grid>
    </Grid>
  </Grid>
);

const CompanyNameInputStep = ({ setCompanyData, setOnboardingState }) => {
  const [user] = useAuth();
  const [existingCompanyWithUserError, setExistingCompanyWithUserError] =
    useState(false);
  const [pendingCompanyMatch, setPendingCompanyMatch] = useState(null);
  const [pendingSearchInput, setPendingSearchInput] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(false);

  // fetchCompanyDataFromName used to always create a new v3-companies doc the
  // moment its exact-name check missed -- called directly via fetchOurApi
  // (bypassing the shared fetchCompanyDataFromName JS helper, which discards
  // everything but companyData, same as the other 3 callers of this endpoint)
  // with checkForDuplicates: true so this can see requiresConfirmation and
  // fall back to a fuzzy match first. The existing hasUsers check below
  // already runs uniformly for every path that resolves to an existing
  // company -- exact match, confirmed-tier fuzzy match, or a user-confirmed
  // candidate-tier match -- since buildExistingCompanyResponse on the backend
  // returns hasUsers/admins the same way for all three.
  const applyCompanyNameSearchResponse = (response) => {
    if (response?.requiresConfirmation) {
      setPendingCompanyMatch({
        matchedCompanyId: response.matchedCompanyId,
        matchedCompanyName: response.matchedCompanyName
      });
      return;
    }

    setPendingCompanyMatch(null);
    const companyData = response?.companyData;
    const { hasUsers } = companyData || {};
    if (hasUsers) {
      sendExistingCompanyAdminEmail(companyData, user);
      setExistingCompanyWithUserError(true);
      return;
    }

    setCompanyData(companyData);
    setOnboardingState("confirm-info");
  };

  const searchCompanyByName = async ({ name, description, mergeDecision }) => {
    const response = await fetchOurApi({
      user,
      path: "/companies/fetch-company-data-from-name",
      method: "POST",
      data: {
        name,
        description,
        checkForDuplicates: true,
        ...(mergeDecision ? { mergeDecision } : {}),
        ...(mergeDecision === "merge" && pendingCompanyMatch
          ? { confirmedMatchedCompanyId: pendingCompanyMatch.matchedCompanyId }
          : {})
      },
      callback: (response) => response
    });

    applyCompanyNameSearchResponse(response);
  };

  const onCompanyNameSearch = (name, description) => {
    setPendingSearchInput({ name, description });
    return searchCompanyByName({ name, description });
  };

  // A fast double-click could otherwise fire two concurrent requests --
  // fetchCompanyDataFromName's exact-match check isn't transactional, so two
  // simultaneous mergeDecision: "new" calls could each miss the other's
  // in-flight write and both create a company doc, defeating the point of
  // this whole confirmation step. Guarded here (not just via the buttons'
  // disabled prop) so it's safe regardless of what triggers this handler.
  const onCompanyMatchDecision = async (mergeDecision) => {
    if (decisionLoading) return;
    setDecisionLoading(true);
    try {
      const { name, description } = pendingSearchInput || {};
      await searchCompanyByName({ name, description, mergeDecision });
    } finally {
      setDecisionLoading(false);
    }
  };

  if (pendingCompanyMatch) {
    return (
      <CompanyMatchConfirmation
        matchedCompanyName={pendingCompanyMatch.matchedCompanyName}
        onDecision={onCompanyMatchDecision}
        decisionLoading={decisionLoading}
      />
    );
  }

  return (
    <Grid container spacing={2} direction="column">
      <Grid item>
        <CompanyDescriptionNameSearch
          onCompanyNameSearch={onCompanyNameSearch}
        />
      </Grid>
      <ExistingCompanyWithUserError
        existingCompanyWithUserError={existingCompanyWithUserError}
      />
    </Grid>
  );
};

const CompanyNameConfirm = ({ dbCompanyData, setOnboardingState }) => {
  const { name } = dbCompanyData || {};

  return (
    <OnboardingImageLayoutBackdrop
      title="Welcome Back"
      subtitle={`It looks like you're creating an account for ${name}. Is this right?`}
      action={
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOnboardingState("confirm-info")}
            >
              Yes, this is my company
            </Button>
          </Grid>
          <Grid item>
            <Button
              color="primary"
              onClick={() => setOnboardingState("select-name")}
            >
              No, I&apos;m with another company
            </Button>
          </Grid>
        </Grid>
      }
      image="/images/platform/onboarding-svg.svg"
      backdropProps={{
        backgroundColor: "rgba(255, 255, 255, 0.95)"
      }}
    />
  );
};

const CompanyOnboardingFlow = ({ dbCompanyData, setCompanyData }) => {
  const initialOnboardingState = isPersonalEmailWebsiteUrl(
    dbCompanyData?.websiteUrl
  )
    ? "select-name"
    : "confirm-name";
  const [onboardingState, setOnboardingState] = useState(
    initialOnboardingState
  );

  if (onboardingState === "confirm-name") {
    return (
      <CompanyNameConfirm
        dbCompanyData={dbCompanyData}
        setOnboardingState={setOnboardingState}
      />
    );
  }

  if (onboardingState === "confirm-info") {
    return <CompanyDataConfirmationStep companyData={dbCompanyData} />;
  }

  return (
    <CompanyNameInputStep
      setCompanyData={setCompanyData}
      setOnboardingState={setOnboardingState}
    />
  );
};

const OnboardingComponent = () => {
  const token = getUrlSearchParams("token");

  const [user, userLoading] = useAuth();
  const { uid, email } = user || {};

  const { verifyAccountLoading } = useContext(OnboardingContext);

  const [verificationStatus, setVerificationStatus] = useState(null);
  const [companyData, setCompanyData] = useState(null);

  useEffect(() => {
    const fetchVerificationStatus = async () =>
      await fetchOurApi({
        path: "/verification/check-verification-status",
        method: "POST",
        data: { uid, token },
        callback: (res) => {
          const { status, companyData } = res;

          if (status === "ready") {
            setCompanyData(companyData);
          }
          return setVerificationStatus(status);
        }
      });

    if (!userLoading && uid && !verificationStatus) {
      fetchVerificationStatus();
    }
  }, [userLoading, uid, verificationStatus, token]);

  const switchOnboardingComponents = () => {
    switch (verificationStatus) {
      case "ready":
        return (
          <CompanyOnboardingFlow
            dbCompanyData={companyData}
            setCompanyData={setCompanyData}
          />
        );
      case "redirect":
        return <Redirect to="/platform/company/dashboard" />;
      default:
        return <ErrorMessage status={verificationStatus} email={email} />;
    }
  };

  const displayContent = () => {
    if (!user && !userLoading) {
      return <OnboardingSiginIn />;
    }

    if (userLoading || !verificationStatus || verifyAccountLoading) {
      return (
        <PageLoadingAnimation
          caption={
            !!verifyAccountLoading &&
            "Please wait while we build your new account"
          }
        />
      );
    }

    return switchOnboardingComponents();
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
    >
      <Container maxWidth="md">{displayContent()}</Container>
    </Box>
  );
};

const OnboardingContextProvider = ({ children }) => {
  const [verifyAccountLoading, setVerifyAccountLoading] = useState(false);

  return (
    <OnboardingContext.Provider
      value={{ verifyAccountLoading, setVerifyAccountLoading }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

const Onboarding = () => (
  <OnboardingContextProvider>
    <OnboardingComponent />
  </OnboardingContextProvider>
);

export default Onboarding;
